import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import React, { FC } from "react";
import {
  CreateButton,
  EditButton,
  Loading,
  ShowActionsProps,
  TopToolbar,
} from "react-admin";
import { apiUrl } from "../../dataProvider";
import useFinance from "../../hooks/useFinance";
import { Booking } from "../../types";
import AcceptCancellationDialog from "./AcceptCancellationDialog";
import AcceptDialog from "./AcceptDialog";
import CancelDialog from "./CancelDialog";
import CancelNoUsageDialog from "./CancelNoUsageDialog";
import CancelReportDialog from "./CancelReportDialog";
import EditInvoiceDialog from "./EditInvoiceDialog";
import FinishAllocating from "./FinishAllocatingDialog";
import FinishDeliveringDialog from "./FinishDeliveringDialog";
import GenrateIssue from "./GenerateIssue";
import OutboundTransactionNumbersDialog from "./OutboundTransactionNumbersDialog";
import ReviseTopUpDialog from "./ReviseTopUpDialog";
import SendInvoiceDialog from "./SendInvoiceDialog";
import ShareIssueButton from "./ShareIssueButton";
import SubmitReportDialog from "./SubmitReportDialog";
import TopUpDialog from "./TopUpDialog";
import UnFinishAllocating from "./UnFinishAllocatingDialog";
import UpdateReportRemarksDialog, {
  isReportSubmitted,
} from "./UpdateReportRemarksDialog";
import { OrderActions, can } from "./util";

export const ShowActions: FC<ShowActionsProps> = ({
  basePath,
  data,
  resource,
}) => {
  const booking = data as Booking;
  const isFinance = useFinance();
  const [printMenuAnchor, setPrintMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [labelDialogOpen, setLabelDialogOpen] = React.useState(false);
  const [labelPage, setLabelPage] = React.useState<string>("");

  React.useEffect(() => console.log("isFinance :>> ", isFinance), [isFinance]);

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handlePrintReport = () => {
    window
      .open(`${apiUrl}/print/bookings/${booking.id}`, "_blank")
      ?.print();
    handlePrintMenuClose();
  };

  const handlePrintLabel = () => {
    handlePrintMenuClose();
    if (process.env.REACT_APP_COMPANY_CODE === "AVANOS") {
      setLabelPage("");
      setLabelDialogOpen(true);
    } else {
      window.open(`${apiUrl}/print/bookings/label/${booking.id}`, "_blank");
    }
  };

  const handleLabelDialogConfirm = () => {
    const pageParam = labelPage ? `?page=${encodeURIComponent(labelPage)}` : "";
    window.open(
      `${apiUrl}/print/bookings/label/${booking.id}${pageParam}`,
      "_blank",
    );
    setLabelDialogOpen(false);
  };

  const handleLabelDialogClose = () => {
    setLabelDialogOpen(false);
  };

  if (!booking) {
    return <Loading />;
  }

  return (
    <TopToolbar>
      {!isFinance && (
        <CreateButton
          basePath={basePath}
          label="Create New"
          icon={<></>}
          color="primary"
        />
      )}

      {process.env.REACT_APP_COMPANY_CODE === "STMY" && (
        <>
          {!isFinance && booking.status === "accepted" && (
            <OutboundTransactionNumbersDialog data={booking} />
          )}
        </>
      )}

      {!isFinance && can(booking, OrderActions.REVISE) && (
        <GenrateIssue data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.REVISE) && (
        <EditButton
          basePath={basePath}
          record={data}
          label="Revise"
          icon={<></>}
          color="primary"
        />
      )}

      <Button
        onClick={(event) => setPrintMenuAnchor(event.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
        style={{ marginLeft: 8, padding: "4px 16px" }}
      >
        Print
      </Button>
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handlePrintReport}>Print Report</MenuItem>
        <MenuItem onClick={handlePrintLabel}>Print Label</MenuItem>
      </Menu>

      {/* Print Label - Page Number Dialog */}
      <Dialog
        open={labelDialogOpen}
        onClose={handleLabelDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Print Label</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Page Number (optional)"
            type="number"
            fullWidth
            value={labelPage}
            onChange={(e) => setLabelPage(e.target.value)}
            inputProps={{ min: 1 }}
            helperText="Leave empty to print all pages"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLabelDialogClose} color="default">
            Cancel
          </Button>
          <Button onClick={handleLabelDialogConfirm} color="primary" variant="contained">
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {!isFinance && can(booking, OrderActions.ACCEPT) && (
        <AcceptDialog data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.UNFINISH_ALLOCATING) && (
        <UnFinishAllocating data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.FINISH_ALLOCATING) && (
        <FinishAllocating data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.FINISH_DELIVERING) && (
        <FinishDeliveringDialog data={booking} />
      )}

      {!isFinance && <ShareIssueButton booking={booking} />}

      {!isFinance &&
        (() => {
          // Check if all reports have been submitted (dates.length === reports.length)
          const datesCount =
            booking.dates && Array.isArray(booking.dates)
              ? booking.dates.length
              : booking.date
                ? 1
                : 0;
          const reportsCount =
            booking.reports && Array.isArray(booking.reports)
              ? booking.reports.length
              : 0;
          const allReportsSubmitted =
            datesCount > 0 && datesCount === reportsCount;

          // Show button if:
          // 1. Status allows SUBMIT_REPORT action (via can() function), OR
          // 2. Status is "report_submitted" but not all surgery dates have reports yet
          const canSubmitReport =
            can(booking, OrderActions.SUBMIT_REPORT) ||
            (booking.status === "report_submitted" && !allReportsSubmitted);

          // Only show button if allowed and not all reports have been submitted
          return canSubmitReport && !allReportsSubmitted ? (
            <SubmitReportDialog data={booking} />
          ) : null;
        })()}

      {/* Finance can only send invoice */}
      {can(booking, OrderActions.SEND_INVOICE) &&
        (() => {
          const datesCount =
            booking.dates && Array.isArray(booking.dates)
              ? booking.dates.length
              : booking.date
                ? 1
                : 0;
          const reportsCount =
            booking.reports && Array.isArray(booking.reports)
              ? booking.reports.length
              : 0;
          const allReportsSubmitted =
            datesCount > 0 && datesCount === reportsCount;

          // After top-up, finance must send invoice even if dates/reports counts
          // no longer align the same way as pre-invoice flow.
          const canShowSendInvoice =
            allReportsSubmitted || booking.status === "topped_up";

          return canShowSendInvoice ? (
            <SendInvoiceDialog data={booking} />
          ) : null;
        })()}

      {/* Edit Invoice button: only show when status is invoiced or invoiced_topped_up */}
      {can(booking, OrderActions.EDIT_INVOICE) && (
        <EditInvoiceDialog data={booking} />
      )}

      {/* Cancel Invoice button: only show when status is invoiced or invoiced_topped_up */}
      {/*
      {!isFinance && (booking.status === "invoiced" || booking.status === "invoiced_topped_up") && (
        <CancelInvoiceDialog data={booking} />
      )}
      */}

      {/* Cancel Report button: only show when status is report_submitted */}
      {!isFinance && booking.status === "report_submitted" && (
        <CancelReportDialog data={booking} />
      )}

      {/* Update Report Remarks button: show when report is submitted or booking has reports */}
      {!isFinance && isReportSubmitted(booking) && (
        <UpdateReportRemarksDialog data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.TOP_UP) && (
        <TopUpDialog data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.CANCEL) && (
        <CancelDialog data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.NO_USAGE) && (
        <CancelNoUsageDialog data={booking} />
      )}

      {!isFinance && can(booking, OrderActions.CANCEL_REQUEST) && (
        <AcceptCancellationDialog data={booking} />
      )}

      {!isFinance &&
        (booking.status === "topped_up" ||
          booking.status === "invoiced_topped_up") && (
          <ReviseTopUpDialog data={booking} />
        )}
    </TopToolbar>
  );
};
